import React, { useEffect } from "react";
import { connect } from "react-redux";
import { setHeading } from "../../../accounts/account.actions";
import { Container } from "react-bootstrap";
import { Grid, Card, CardContent, Typography, Box, Avatar, Divider } from "@mui/material";
import { DashboardDataApiIfStale } from "./AccountsDashboardAction";
import { startFinancialDate, endFinancialDate, FinancialYear, GlobalSpinner } from "../../../../../global/globalFunctions";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ComposedChart, Line, Area, Label, Legend, CartesianGrid, PieChart, Pie } from "recharts";



const EXP_COLORS = ["#6366f1", "#8b5cf6", "#f59e0b", "#f97316", "#ef4444", "#10b981"];
const REV_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#6366f1", "#f97316"];
const cardStyles = [
    {
        icon: <ShoppingBagOutlinedIcon />,
        color: "#2563EB",
        gradient: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
    },
    {
        icon: <PeopleOutlineIcon />,
        color: "#7C3AED",
        gradient: "linear-gradient(135deg, #f3e5f5, #ce93d8)",
    },
    {
        icon: <ShoppingCartOutlinedIcon />,
        color: "#D97706",
        gradient: "linear-gradient(135deg, #fff8e1, #ffe082)",
    },
    {
        icon: <EmailOutlinedIcon />,
        color: "#EF4444",
        gradient: "linear-gradient(135deg, #ffebee, #ef9a9a)",
    },
];

const AccountsDashboard = (props) => {
    const { setHeading, DashboardDataApiIfStale, dashboardDataList } = props;

    useEffect(() => {
        setHeading("Dashboard");
        DashboardDataApiIfStale({
            startDate: startFinancialDate,
            endDate: endFinancialDate,
        });
    }, []);

    if (!dashboardDataList) {
        return <GlobalSpinner />;
    }

    const assetsData = dashboardDataList?.assetsChartData || [];
    const assetsTotal = assetsData.reduce((sum, item) => sum + item.value, 0);
    const colors = ["#6366f1", "#22c55e", "#f59e0b", "#f97316"];

    const composedData = dashboardDataList?.monthlyPerformanceData || [];

    const expenseBreakdown = dashboardDataList?.revenueExpenseBreakdown?.expenses || [];
    const revenueBreakdown = dashboardDataList?.revenueExpenseBreakdown?.revenue || [];
    const expTotal = expenseBreakdown.reduce((sum, item) => sum + item.value, 0);
    const revTotal = revenueBreakdown.reduce((sum, item) => sum + item.value, 0);

    // ---------- Map Data to Summary Cards ----------
    const summaryCards = dashboardDataList?.accounts?.map((card, i) => {
        const style = cardStyles[i % cardStyles.length];
        return { ...card, ...style };
    }) || [];

    return (
        <Container fluid>
            {/* ---------- Financial Year Header ---------- */}
            <Box sx={{
                maxWidth: 500, mx: "auto", my: 2, p: 2, textAlign: "center", borderRadius: 3, bgcolor: "#fff", boxShadow: 2, border: "1px solid #e0e0e0", marginTop: 0,
            }} >
                <Typography variant="h6" fontWeight="bold" color="#1e293b"> Financial Year: {FinancialYear}</Typography>
                <Divider sx={{ width: 80, mx: "auto", mt: 1, borderColor: "#1565C0", borderWidth: 3, borderRadius: 2 }} />
            </Box>

            {/* ---------- Summary Cards ---------- */}
            <Grid container spacing={2}
                sx={{
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    pb: 2,
                    "&::-webkit-scrollbar": { height: 8 },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#c1c1c1",
                        borderRadius: 4,
                    },
                }}
            >
                {summaryCards.map((card, i) => (
                    <Grid key={i} item xs="auto" sx={{ minWidth: 240 }}>
                        <Card sx={{ borderRadius: 4, background: card.gradient, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", height: "100%" }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Avatar sx={{ bgcolor: "#fff", color: card.color, width: 48, height: 48, boxShadow: 2 }}>
                                        {card.icon}
                                    </Avatar>

                                    <Box display="flex" alignItems="center">
                                        {card.levelBalanceSign === "Debit" ? (
                                            <ArrowDropUpIcon sx={{ color: "green" }} />
                                        ) : (
                                            <ArrowDropDownIcon sx={{ color: "red" }} />
                                        )}
                                        <Typography
                                            variant="body1"
                                            color={card.levelBalanceSign === "Debit" ? "green" : "red"}
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {card.levelBalanceSign}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Typography variant="body2" sx={{ mt: 2, color: "#555", fontWeight: 500 }}>
                                    {card.name}
                                </Typography>

                                <Typography sx={{ fontWeight: 700, color: "#111", fontSize: "1.5rem", pt: 1 }}>
                                    {card.levelBalance}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* ---------- Monthly Income + Income Distribution ---------- */}
            <Box sx={{ width: "100%", mt: 3, mb: 3, borderRadius: "20px", background: "#fff", border: "1px solid #0000000f", boxShadow: "0 2px 12px #0000000f", overflow: "hidden" }}>
                <Box sx={{ px: 3, pt: 2.5, pb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #0000000d", }} >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                background: "linear-gradient(135deg, #0f2044 0%, #1565C0 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "16px",
                            }}
                        >
                            📊
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: "10px", fontWeight: 600, letterSpacing: "1.2px", textTransform: "uppercase", color: "#94a3b8", lineHeight: 1, mb: 0.3 }} >
                                Analytics
                            </Typography>
                            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.3px", lineHeight: 1, }}>
                                Monthly Income & Distribution
                            </Typography>
                        </Box>
                    </Box>

                    {/* Legend pills */}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {["#6366f1", "#22c55e", "#f59e0b", "#f97316"].map((color, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    px: 1.2,
                                    py: 0.4,
                                    borderRadius: "20px",
                                    background: `${color}14`,
                                    border: `1px solid ${color}30`,
                                }}
                            >
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color }} />
                                <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#475569" }}>
                                    Q{i + 1}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Two Charts Row */}
                <Box sx={{ display: "flex", alignItems: "stretch", gap: 0, }}>
                    <Box sx={{ flex: "0 0 70%", px: 2, pt: 2, pb: 3, borderRight: "1px solid rgba(0,0,0,0.05)", }} >
                        <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "#cbd5e1", mb: 1, pl: 1, }} >
                            Monthly Breakdown
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={dashboardDataList?.barData || []} margin={{ top: 24, right: 20, bottom: 4, left: 0 }} barCategoryGap="35%" >
                                <defs>
                                    {["#6366f1", "#22c55e", "#f59e0b", "#f97316"].map((color, i) => (
                                        <linearGradient key={i} id={`barGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={color} stopOpacity={1} />
                                            <stop offset="100%" stopColor={color} stopOpacity={0.55} />
                                        </linearGradient>
                                    ))}
                                </defs>
                                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="rgba(0,0,0,0.05)" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fontWeight: 600, fill: "#94a3b8" }} dy={8} />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 11, fontWeight: 500, fill: "#94a3b8" }}
                                    width={48}
                                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                                />
                                <Tooltip
                                    cursor={{ fill: "rgba(99,102,241,0.05)", radius: 8 }}
                                    contentStyle={{
                                        borderRadius: "12px",
                                        border: "1px solid rgba(0,0,0,0.08)",
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                                        padding: "10px 16px",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: "#0f172a",
                                        background: "#fff",
                                    }}
                                    labelStyle={{
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: "#64748b",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.8px",
                                        marginBottom: "4px",
                                    }}
                                    itemStyle={{ color: "#0f172a", fontWeight: 700 }}
                                />
                                <Bar
                                    dataKey="value"
                                    radius={[8, 8, 0, 0]}
                                    maxBarSize={52}
                                    label={{
                                        position: "top",
                                        fill: "#64748b",
                                        fontSize: 11,
                                        fontWeight: 700,
                                        formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v),
                                    }}
                                >
                                    {dashboardDataList?.barData?.map((_, i) => (
                                        <Cell key={i} fill={`url(#barGrad${i % 4})`} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>

                    {/* ── RIGHT: Donut Chart (30%) ── */}
                    <Box sx={{ flex: "0 0 30%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", px: 2, pt: 2, pb: 3, gap: 1.5, }}>
                        <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "#cbd5e1", alignSelf: "flex-start", }}>
                            Distribution
                        </Typography>

                        {/* Donut Chart */}
                        <Box sx={{ position: "relative", width: "100%" }}>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <defs>
                                        {colors.map((color, index) => (
                                            <linearGradient key={index} id={`donutGrad${index}`} x1="0" y1="0" x2="1" y2="1">
                                                <stop offset="0%" stopColor={color} />
                                                <stop offset="100%" stopColor={color} />
                                            </linearGradient>
                                        ))}
                                    </defs>

                                    <Tooltip
                                        contentStyle={{ borderRadius: "12px", border: "1px solid #00000014", boxShadow: "0 8px 24px #0000001a", padding: "8px 14px", background: "#fff", fontSize: "12px", fontWeight: 600, }}
                                        itemStyle={{ color: "#0f172a" }}
                                    />

                                    <Pie
                                        dataKey="value"
                                        data={assetsData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={4}
                                        strokeWidth={0}
                                    >
                                        {assetsData.map((_, index) => (
                                            <Cell key={index} fill={`url(#donutGrad${index})`} />
                                        ))}
                                    </Pie>

                                </PieChart>
                            </ResponsiveContainer>

                            {/* Center Label */}
                            {assetsTotal > 0 && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        textAlign: "center",
                                        pointerEvents: "none",
                                    }}
                                >
                                    <Typography sx={{ fontSize: "12px", fontWeight: 900, color: "#0f172a", lineHeight: 1 }}>
                                        {assetsTotal.toLocaleString()}
                                    </Typography>
                                    <Typography sx={{ fontSize: "9px", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.8px", textTransform: "uppercase", mt: 0.3 }}>
                                        Total
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Percentage Lines */}
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.8 }}>
                            {assetsData.map((item, index) => {
                                const pct = assetsTotal ? Math.round((item.value / assetsTotal) * 100) : 0;
                                return (
                                    <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 1 }} >
                                        <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: colors[index], flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#475569", flex: 1 }}>
                                            {item.name}
                                        </Typography>
                                        <Box sx={{ flex: 2, height: 4, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                                            <Box
                                                sx={{
                                                    width: `${pct}%`,
                                                    height: "100%",
                                                    borderRadius: 4,
                                                    background: colors[index],
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            sx={{
                                                fontSize: "10px",
                                                fontWeight: 700,
                                                color: "#94a3b8",
                                                minWidth: 28,
                                                textAlign: "right",
                                            }}
                                        >
                                            {pct}%
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                    </Box>
                </Box>
            </Box>

            {/* ---------- Sales vs Profit vs Target ---------- */}
            <Box
                sx={{
                    width: "100%",
                    mt: 3,
                    mb: 3,
                    borderRadius: "20px",
                    background: "#fff",
                    border: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                }}
            >
                {/* Card Header */}
                <Box
                    sx={{
                        px: 3,
                        pt: 2.5,
                        pb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid rgba(0,0,0,0.05)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                background: "linear-gradient(135deg, #0f2044 0%, #1565C0 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "16px",
                            }}
                        >
                            📈
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    letterSpacing: "1.2px",
                                    textTransform: "uppercase",
                                    color: "#94a3b8",
                                    lineHeight: 1,
                                    mb: 0.3,
                                }}
                            >
                                Performance Overview
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "15px",
                                    fontWeight: 800,
                                    color: "#0f172a",
                                    letterSpacing: "-0.3px",
                                    lineHeight: 1,
                                }}
                            >
                                Sales vs Profit vs Target
                            </Typography>
                        </Box>
                    </Box>

                    {/* Legend Pills */}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {[
                            { label: "Target", color: "#6366f1" },
                            { label: "Sales", color: "#1565C0" },
                            { label: "Profit", color: "#f97316" },
                        ].map(({ label, color }) => (
                            <Box
                                key={label}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.6,
                                    px: 1.4,
                                    py: 0.5,
                                    borderRadius: "20px",
                                    background: `${color}12`,
                                    border: `1px solid ${color}30`,
                                }}
                            >
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color }} />
                                <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#475569" }}>
                                    {label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Chart Area */}
                <Box sx={{ px: 2, pt: 2, pb: 3 }}>
                    <ResponsiveContainer width="100%" height={340}>
                        <ComposedChart
                            data={composedData}
                            margin={{ top: 20, right: 24, bottom: 32, left: 16 }}
                        >
                            <defs>
                                <linearGradient id="targetGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.25} />
                                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                                </linearGradient>
                                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#1565C0" stopOpacity={1} />
                                    <stop offset="100%" stopColor="#1565C0" stopOpacity={0.6} />
                                </linearGradient>
                            </defs>

                            <CartesianGrid
                                strokeDasharray="4 4"
                                vertical={false}
                                stroke="rgba(0,0,0,0.05)"
                            />

                            <XAxis
                                dataKey="month"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fontWeight: 600, fill: "#94a3b8" }}
                                dy={10}
                            >
                                <Label
                                    value="Months"
                                    offset={-16}
                                    position="insideBottom"
                                    style={{
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        fill: "#64748b",
                                        letterSpacing: "1px",
                                        textTransform: "uppercase",
                                    }}
                                />
                            </XAxis>

                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fontWeight: 500, fill: "#94a3b8" }}
                                width={52}
                                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                            >
                                <Label
                                    value="Values"
                                    angle={-90}
                                    position="insideLeft"
                                    offset={8}
                                    style={{
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        fill: "#64748b",
                                        letterSpacing: "1px",
                                        textTransform: "uppercase",
                                    }}
                                />
                            </YAxis>

                            <Tooltip
                                cursor={{ fill: "rgba(99,102,241,0.04)", radius: 8 }}
                                contentStyle={{
                                    borderRadius: "12px",
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                                    padding: "10px 16px",
                                    background: "#fff",
                                }}
                                labelStyle={{
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.8px",
                                    marginBottom: "6px",
                                }}
                                itemStyle={{ fontSize: "12px", fontWeight: 600, color: "#0f172a" }}
                            />

                            {/* Target Area */}
                            <Area
                                type="monotone"
                                dataKey="target"
                                fill="url(#targetGrad)"
                                stroke="#6366f1"
                                strokeWidth={2}
                                strokeDasharray="5 4"
                                dot={false}
                                activeDot={{ r: 5, fill: "#6366f1", strokeWidth: 0 }}
                                name="Target"
                            />

                            {/* Sales Bars */}
                            <Bar
                                dataKey="sales"
                                barSize={28}
                                fill="url(#salesGrad)"
                                radius={[6, 6, 0, 0]}
                                name="Sales"
                            />

                            {/* Profit Line */}
                            <Line
                                type="monotone"
                                dataKey="profit"
                                stroke="#f97316"
                                strokeWidth={2.5}
                                dot={{ r: 4, fill: "#f97316", strokeWidth: 0 }}
                                activeDot={{ r: 6, fill: "#f97316", strokeWidth: 2, stroke: "#fff" }}
                                name="Profit"
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            {/* ---------- Revenue & Expense Breakdown ---------- */}
            <Box
                sx={{
                    width: "100%",
                    mt: 3,
                    mb: 3,
                    borderRadius: "20px",
                    background: "#fff",
                    border: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                }}
            >
                {/* Card Header */}
                <Box
                    sx={{
                        px: 3,
                        pt: 2.5,
                        pb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid rgba(0,0,0,0.05)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                background: "linear-gradient(135deg, #0f2044 0%, #1565C0 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "16px",
                            }}
                        >
                            🥧
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    letterSpacing: "1.2px",
                                    textTransform: "uppercase",
                                    color: "#94a3b8",
                                    lineHeight: 1,
                                    mb: 0.3,
                                }}
                            >
                                Breakdown
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "15px",
                                    fontWeight: 800,
                                    color: "#0f172a",
                                    letterSpacing: "-0.3px",
                                    lineHeight: 1,
                                }}
                            >
                                Revenue & Expense
                            </Typography>
                        </Box>
                    </Box>

                    {/* Ring Labels */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {[
                            { label: "Expenses", color: "#6366f1", ring: "Inner" },
                            { label: "Revenue", color: "#0ea5e9", ring: "Outer" },
                        ].map(({ label, color }) => (
                            <Box
                                key={label}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.6,
                                    px: 1.4,
                                    py: 0.5,
                                    borderRadius: "20px",
                                    background: `${color}12`,
                                    border: `1px solid ${color}30`,
                                }}
                            >
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color }} />
                                <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#475569" }}>
                                    {label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Chart + Legend Side by Side */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        px: 3,
                        pt: 2,
                        pb: 3,
                    }}
                >
                    {/* Pie Chart */}
                    <Box sx={{ flex: "1 1 280px", minWidth: 260 }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <defs>
                                    {EXP_COLORS.map((c, i) => (
                                        <linearGradient key={`eg${i}`} id={`eg${i}`} x1="0" y1="0" x2="1" y2="1">
                                            <stop offset="0%" stopColor={c} />
                                            <stop offset="100%" stopColor={c} stopOpacity={0.7} />
                                        </linearGradient>
                                    ))}
                                    {REV_COLORS.map((c, i) => (
                                        <linearGradient key={`rg${i}`} id={`rg${i}`} x1="0" y1="0" x2="1" y2="1">
                                            <stop offset="0%" stopColor={c} />
                                            <stop offset="100%" stopColor={c} stopOpacity={0.7} />
                                        </linearGradient>
                                    ))}
                                </defs>

                                <Tooltip
                                    contentStyle={{
                                        borderRadius: "12px",
                                        border: "1px solid rgba(0,0,0,0.08)",
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                                        padding: "10px 16px",
                                        background: "#fff",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                    }}
                                    labelStyle={{ color: "#64748b" }}
                                    itemStyle={{ color: "#0f172a" }}
                                />

                                {/* Inner Ring — Expenses */}
                                <Pie
                                    dataKey="value"
                                    data={expenseBreakdown.length ? expenseBreakdown : [{ name: "No Data", value: 1 }]}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={58}
                                    outerRadius={90}
                                    paddingAngle={3}
                                    label={false}
                                    strokeWidth={0}
                                >
                                    {(expenseBreakdown.length ? expenseBreakdown : [{}]).map((_, i) => (
                                        <Cell key={i} fill={expenseBreakdown.length ? `url(#eg${i % EXP_COLORS.length})` : "#e2e8f0"} />
                                    ))}
                                </Pie>

                                {/* Outer Ring — Revenue */}
                                <Pie
                                    dataKey="value"
                                    data={revenueBreakdown.length ? revenueBreakdown : [{ name: "No Data", value: 1 }]}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={98}
                                    outerRadius={130}
                                    paddingAngle={3}
                                    label={false}
                                    strokeWidth={0}
                                >
                                    {(revenueBreakdown.length ? revenueBreakdown : [{}]).map((_, i) => (
                                        <Cell key={i} fill={revenueBreakdown.length ? `url(#rg${i % REV_COLORS.length})` : "#e2e8f0"} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>

                    {/* Custom Legend Panel */}
                    <Box sx={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: 2 }}>

                        {/* Expenses */}
                        <Box>
                            <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "#94a3b8", mb: 1 }}>
                                Expenses (Inner)
                            </Typography>
                            {expenseBreakdown.length === 0 && (
                                <Typography sx={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>No expense data</Typography>
                            )}
                            {expenseBreakdown.map(({ name, value }, i) => {
                                const pct = expTotal ? Math.round((value / expTotal) * 100) : 0;
                                const color = EXP_COLORS[i % EXP_COLORS.length];
                                return (
                                    <Box key={name} sx={{ mb: 1 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: "11.5px", fontWeight: 600, color: "#334155" }}>{name}</Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#64748b" }}>{pct}%</Typography>
                                        </Box>
                                        <Box sx={{ height: 4, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                                            <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 4, background: color, transition: "width 0.6s ease" }} />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box sx={{ height: "1px", background: "rgba(0,0,0,0.06)" }} />

                        {/* Revenue */}
                        <Box>
                            <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "#94a3b8", mb: 1 }}>
                                Revenue (Outer)
                            </Typography>
                            {revenueBreakdown.length === 0 && (
                                <Typography sx={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>No revenue data</Typography>
                            )}
                            {revenueBreakdown.map(({ name, value }, i) => {
                                const pct = revTotal ? Math.round((value / revTotal) * 100) : 0;
                                const color = REV_COLORS[i % REV_COLORS.length];
                                return (
                                    <Box key={name} sx={{ mb: 1 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: "11.5px", fontWeight: 600, color: "#334155" }}>{name}</Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#64748b" }}>{pct}%</Typography>
                                        </Box>
                                        <Box sx={{ height: 4, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                                            <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 4, background: color, transition: "width 0.6s ease" }} />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </Box>


        </Container>
    );
};

const mapDispatchToProps = (dispatch) => ({
    DashboardDataApiIfStale: (payload) => dispatch(DashboardDataApiIfStale(payload)),
    setHeading: (data) => dispatch(setHeading(data)),
});

const mapStateToProps = (state) => ({
    dashboardDataList: state.AccountsDashboardReducer.dashboardDataList,
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountsDashboard);